/**
 * PursuitLifecycleStepper — v3.18.11
 *
 * Bird's-eye 9-dot lifecycle stepper that sits between the workspace
 * navbar and each pursuit page. Derives the active step from the URL,
 * and progress from `pursuit.status`.
 *
 * Visual contract:
 *   ● completed (emerald + check)  → past stages
 *   ◉ current   (emerald + ring)   → matches pursuit.status
 *   ○ active    (slate + bold)     → the page the user is currently viewing
 *                                    (this is independent of pursuit.status —
 *                                     you can be VIEWING strategy while the
 *                                     pursuit's status is still QUALIFICATION)
 *   ○ upcoming  (slate, light)     → future stages
 *   ◌ skipped   (slate, dashed)    → non-RFI pursuits dim Capability Statement
 *
 * Click any dot to deep-link to that stage page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { usePursuit } from '../../contexts/PursuitContext';
import { usePursuitLayout } from '../../contexts/PursuitLayoutContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Order matches Sidebar.jsx — the canonical federal capture lifecycle.
// v3.18.16 — Split compliance into two distinct steps:
//   • Compliance Matrix (Section L/M shred) — between Strategy and Proposal
//   • Submission       (post-draft format & delivery) — final stage
// The 9-step flow becomes 10-step. Both map to the same backing page
// (`/compliance`) but open different tabs via `?tab=`.
const STEPS = [
  { id: 'intake',               path: 'intake',                  label: 'Intake',          short: 'Int'  },
  { id: 'ingestion',            path: 'ingestion',               label: 'Ingestion',       short: 'Ing'  },
  { id: 'capability_statement', path: 'capability-statement',    label: 'Cap Stmt',        short: 'Cap'  },
  { id: 'agency_questions',     path: 'questions',               label: 'Q & A',           short: 'Q&A'  },
  { id: 'gonogo',               path: 'gonogo',                  label: 'Go/No-Go',        short: 'G/N'  },
  { id: 'gaps',                 path: 'gaps',                    label: 'Gaps',            short: 'Gap'  },
  { id: 'strategy',             path: 'strategy',                label: 'Strategy',        short: 'Str'  },
  { id: 'compliance_matrix',    path: 'compliance',              label: 'Compl. Matrix',   short: 'CMx'  },
  { id: 'proposal',             path: 'proposal',                label: 'Proposal',        short: 'Prop' },
  { id: 'submission',           path: 'compliance?tab=submission-day', label: 'Submission', short: 'Sub' },
];

// pursuit.status → STEPS index of the "current" lifecycle position.
// v3.18.16 — DRAFTING now maps to index 8 (Proposal moved from 7 to 8 with
// the Compliance Matrix insertion). SUBMITTED maps to the new Submission
// step at index 9.
const STATUS_TO_INDEX = {
  INTAKE:        0,
  QUALIFICATION: 4,
  STRATEGY:      6,
  DRAFTING:      8,
  SUBMITTED:     9,
};

export default function PursuitLifecycleStepper({ isTopBar = false }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { workspaceId, pursuitId } = useParams();
  const { authToken }  = useAuth();
  const { activePursuit } = usePursuit();
  const { topBarActive } = usePursuitLayout();

  // All hooks must be called before any early return (Rules of Hooks).
  const [status, setStatus] = useState(activePursuit?.status || null);
  const [noticeType, setNoticeType] = useState(activePursuit?.notice_type || null);
  const [farPhase, setFarPhase] = useState(activePursuit?.far_phase || null);

  // Fetch once when the context didn't include status / notice_type /
  // far_phase. RFI eligibility is true if EITHER notice_type is 'RFI' OR
  // the FAR lifecycle phase has been set to RFI (Sources Sought).
  useEffect(() => {
    if ((status && noticeType && farPhase) || !pursuitId || !authToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        if (!status) setStatus(data?.status || null);
        if (!noticeType) setNoticeType(data?.notice_type || null);
        if (!farPhase) setFarPhase(data?.far_phase || null);
      } catch (_) { /* silent — stepper just renders without progress */ }
    })();
    return () => { cancelled = true; };
  }, [pursuitId, authToken, status, noticeType, farPhase]);

  // Which step the URL is on right now. Falls back to -1 (no page match).
  // v3.18.16 — `compliance` matches two distinct steps; disambiguate via
  // the `?tab=` query.
  const viewingIdx = useMemo(() => {
    const seg = location.pathname.split('/').filter(Boolean).pop();
    if (seg === 'compliance') {
      const tab = new URLSearchParams(location.search || '').get('tab');
      const stepId = tab === 'submission-day' ? 'submission' : 'compliance_matrix';
      return STEPS.findIndex((s) => s.id === stepId);
    }
    return STEPS.findIndex((s) => s.path === seg);
  }, [location.pathname, location.search]);

  // Status-derived progress index. WON/LOST mark all completed.
  const currentIdx = useMemo(() => {
    if (status === 'WON' || status === 'LOST') return STEPS.length; // all done
    if (status === 'NO_BID') return -1;                              // none completed
    return STATUS_TO_INDEX[status] ?? -1;
  }, [status]);

  // Don't render if we're not on a pursuit page that matches a step.
  if (!workspaceId || !pursuitId || viewingIdx < 0) return null;

  // When the layout top bar is active, inline instances suppress themselves
  // so there's only one stepper on screen. (Must be after all hooks.)
  if (topBarActive && !isTopBar) return null;

  const isRfi = noticeType === 'RFI' || farPhase === 'RFI';
  const currentLabel = STEPS[Math.max(viewingIdx, 0)]?.label || '';

  // ── Top-bar compact variant ────────────────────────────────────────────
  if (isTopBar) {
    return (
      <div className="flex items-center" data-testid="pursuit-lifecycle-stepper-topbar">
        <ol className="flex items-center gap-0.5" role="list">
          {STEPS.map((step, idx) => {
            const isSkipped  = step.id === 'capability_statement' && !isRfi;
            const isViewing  = idx === viewingIdx;
            const isCompleted = currentIdx >= 0 && idx < currentIdx && !isSkipped;
            const isCurrent   = currentIdx === idx && !isSkipped;

            const dotClasses = cn(
              'w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 transition-all',
              'ring-1 ring-inset',
              isCompleted && 'bg-emerald-500 text-white ring-emerald-500',
              isCurrent   && 'bg-emerald-100 text-emerald-700 ring-emerald-500 ring-2',
              isViewing && !isCurrent && 'bg-slate-800 text-white ring-slate-800',
              !isCompleted && !isCurrent && !isViewing && !isSkipped && 'bg-white text-slate-400 ring-slate-300',
              isSkipped   && 'bg-white text-slate-200 ring-slate-200 ring-dashed opacity-50',
            );
            const connectorClasses = cn(
              'w-3 h-px',
              idx <= currentIdx - 1 ? 'bg-emerald-400' : 'bg-slate-200',
            );

            return (
              <React.Fragment key={step.id}>
                <li title={step.label}>
                  <button
                    type="button"
                    onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/${step.path}`)}
                    className={dotClasses}
                    aria-label={`Go to ${step.label}`}
                    aria-current={isCurrent || isViewing ? 'step' : undefined}
                    data-testid={`stepper-dot-topbar-${step.id}`}
                  >
                    {isCompleted ? <Check className="w-3 h-3" strokeWidth={3} /> : idx + 1}
                  </button>
                </li>
                {idx < STEPS.length - 1 && <div className={connectorClasses} />}
              </React.Fragment>
            );
          })}
        </ol>
        {/* Current step label */}
        <span className="ml-3 text-xs font-semibold text-slate-700 hidden sm:block">
          {STEPS[Math.max(viewingIdx, 0)]?.label}
        </span>
      </div>
    );
  }

  // ── Default inline card variant ────────────────────────────────────────
  return (
    <div
      className="my-5 rounded-lg border border-slate-200 bg-white shadow-sm"
      data-testid="pursuit-lifecycle-stepper"
    >
      <div className="px-4 py-3">
        <ol className="flex items-center gap-1 sm:gap-2" role="list">
          {STEPS.map((step, idx) => {
            const isSkipped = step.id === 'capability_statement' && !isRfi;
            const isViewing = idx === viewingIdx;
            const isCompleted = currentIdx >= 0 && idx < currentIdx && !isSkipped;
            const isCurrent = currentIdx === idx && !isSkipped;
            const isUpcoming = !isCompleted && !isCurrent && !isViewing && !isSkipped;

            // Color tokens
            const dotClasses = cn(
              'w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold transition-all flex-shrink-0',
              'ring-1 ring-inset',
              isCompleted && 'bg-emerald-500 text-white ring-emerald-500',
              isCurrent   && 'bg-emerald-100 text-emerald-700 ring-emerald-500 ring-2',
              isViewing && !isCurrent && 'bg-slate-900 text-white ring-slate-900',
              isUpcoming  && 'bg-white text-slate-400 ring-slate-300',
              isSkipped   && 'bg-white text-slate-300 ring-slate-200 ring-dashed opacity-60',
            );

            const connectorClasses = cn(
              'flex-1 h-0.5 min-w-[8px]',
              idx <= currentIdx - 1 ? 'bg-emerald-500' : 'bg-slate-200',
            );

            return (
              <React.Fragment key={step.id}>
                <li className="flex flex-col items-center gap-0.5 group">
                  <button
                    type="button"
                    onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/${step.path}`)}
                    className={dotClasses}
                    title={
                      isSkipped
                        ? `${step.label} — only used for RFI pursuits`
                        : isCompleted
                        ? `${step.label} — completed`
                        : isCurrent
                        ? `${step.label} — current stage`
                        : isViewing
                        ? `${step.label} — you're viewing this`
                        : step.label
                    }
                    aria-label={`Go to ${step.label}`}
                    aria-current={isCurrent || isViewing ? 'step' : undefined}
                    data-testid={`stepper-dot-${step.id}`}
                  >
                    {isCompleted ? (
                      <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    ) : (
                      idx + 1
                    )}
                  </button>
                  <span
                    className={cn(
                      'text-[10px] leading-tight hidden md:block',
                      isViewing && 'font-semibold text-slate-900',
                      isCurrent && !isViewing && 'font-semibold text-emerald-700',
                      isCompleted && 'text-slate-500',
                      isUpcoming && 'text-slate-400',
                      isSkipped  && 'text-slate-300 line-through',
                    )}
                  >
                    {step.short}
                  </span>
                </li>
                {idx < STEPS.length - 1 && <div className={connectorClasses} />}
              </React.Fragment>
            );
          })}
        </ol>
        <p className="text-[11px] text-slate-500 mt-1.5 text-center md:hidden">
          Step {viewingIdx + 1} of {STEPS.length}: <strong className="text-slate-700">{currentLabel}</strong>
        </p>
      </div>
    </div>
  );
}
